/**
 * Núcleo do ditado por voz, sem React e sem dependência de browser.
 * Tudo o que toca a plataforma entra por `VoiceEnvironment`, o que deixa a lógica
 * interessante (formato, tetos, limpeza do stream, transcrição) testável em Node.
 */
export interface TrackLike { stop(): void }
export interface StreamLike { getTracks(): TrackLike[] }
export interface RecorderLike {
  readonly state: string;
  start(timeslice?: number): void;
  stop(): void;
  ondataavailable: ((event: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: (() => void) | null;
}
export interface VoiceEnvironment {
  getStream(): Promise<StreamLike>;
  isTypeSupported(mimeType: string): boolean;
  createRecorder(stream: StreamLike, mimeType: string): RecorderLike;
  createBlob(chunks: Blob[], mimeType: string): Blob;
  transcribe(audio: Blob): Promise<{ text: string }>;
}
export interface VoiceRecorderHandlers {
  onRecordingChange?(recording: boolean): void;
  onTranscribingChange?(transcribing: boolean): void;
  onText?(text: string): void;
  onError?(error: Error): void;
}

/** Mesmos limites já praticados pelo compositor do chat. */
export const PREFERRED_MIME_TYPES = ["audio/webm;codecs=opus", "audio/mp4"];
export const MAX_RECORDING_BYTES = 19 * 1024 * 1024;
export const MAX_RECORDING_SECONDS = 180;
export const UNSUPPORTED_FORMAT = "Este navegador não oferece um formato de gravação compatível.";
export const RECORDING_FAILED = "Não foi possível gravar o áudio.";

/** O texto ditado é acrescentado ao que já existe, nunca substitui o que a pessoa escreveu. */
export function appendDictation(current: string, addition: string, maxChars: number) {
  return `${current}${current ? "\n" : ""}${addition}`.slice(0, maxChars);
}

export class VoiceRecorder {
  private recorder?: RecorderLike;
  private stream?: StreamLike;
  private active = false;
  private disposed = false;

  constructor(private readonly env: VoiceEnvironment, private readonly handlers: VoiceRecorderHandlers = {}) {}

  get recording() { return this.active; }

  async start() {
    if (this.active || this.disposed) return;
    let stream: StreamLike | undefined;
    try {
      stream = await this.env.getStream();
      this.stream = stream;
      const mimeType = PREFERRED_MIME_TYPES.find((type) => this.env.isTypeSupported(type));
      if (!mimeType) throw new Error(UNSUPPORTED_FORMAT);
      // A permissão do microfone é assíncrona: a tela pode ter sido desmontada nesse meio-tempo.
      if (this.disposed) { this.releaseStream(); return; }

      const recorder = this.env.createRecorder(stream, mimeType);
      const chunks: Blob[] = [];
      let size = 0;
      this.recorder = recorder;

      recorder.ondataavailable = (event) => {
        size += event.data.size;
        chunks.push(event.data);
        if (size > MAX_RECORDING_BYTES && recorder.state === "recording") recorder.stop();
      };
      recorder.onerror = () => {
        this.releaseStream();
        this.recorder = undefined;
        this.setRecording(false);
        this.handlers.onError?.(new Error(RECORDING_FAILED));
      };
      recorder.onstop = async () => {
        this.releaseStream();
        this.recorder = undefined;
        if (this.disposed) return;
        this.setRecording(false);
        this.handlers.onTranscribingChange?.(true);
        try {
          const result = await this.env.transcribe(this.env.createBlob(chunks, mimeType));
          if (!this.disposed) this.handlers.onText?.(result.text);
        } catch (error) {
          if (!this.disposed) this.handlers.onError?.(error instanceof Error ? error : new Error(RECORDING_FAILED));
        } finally {
          if (!this.disposed) this.handlers.onTranscribingChange?.(false);
        }
      };

      recorder.start(1000);
      this.setRecording(true);
    } catch (error) {
      stream?.getTracks().forEach((track) => track.stop());
      this.stream = undefined;
      this.handlers.onError?.(error instanceof Error ? error : new Error(RECORDING_FAILED));
    }
  }

  stop() {
    if (this.active && this.recorder) this.recorder.stop();
  }

  /** Desmontagem: solta o microfone e não entrega mais transcrição nem estado. */
  dispose() {
    this.disposed = true;
    if (this.recorder) {
      this.recorder.onstop = null;
      this.recorder.stop();
      this.recorder = undefined;
    }
    this.releaseStream();
    this.active = false;
  }

  private setRecording(value: boolean) {
    this.active = value;
    this.handlers.onRecordingChange?.(value);
  }

  private releaseStream() {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = undefined;
  }
}
